import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as faceapi from "face-api.js";
import { loadFaceModels } from "../lib/faceModels";
import {
  ArrowLeft,
  Briefcase,
  User,
  Clock,
  Pencil,
  ScanFace,
  Camera,
  CheckCircle2,
  KeyRound,
  Wallet,
  Plane,
  Coins,
  Receipt as ReceiptIcon,
  Printer,
  FileText,
  Upload,
  Download,
  Trash2,
  FileIcon,
  UserX,
  UserCheck,
  Loader2,
  Plus,
  X,
  Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";
import DatePickerEs from "../components/DatePickerEs";
import { uploadFile } from "../lib/files";
import AuthImage from "../components/AuthImage";
import { SignatureImage, printElement } from "../components/SignaturePad";
import EmployeeVacationsTab from "../components/EmployeeVacationsTab";
import EmployeeSalariesTab from "../components/EmployeeSalariesTab";
import { WorkScheduleEditor } from "../components/WorkScheduleEditor";
import { SalaryBreakdown, OvertimeBreakdown, MonthlyPayEstimate } from "../components/SalaryBreakdown";

const statusColors = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  inactive: "bg-zinc-100 text-zinc-500 border-zinc-200",
  suspended: "bg-amber-50 text-amber-700 border-amber-200",
  terminated: "bg-red-50 text-red-700 border-red-200",
};
const statusLabels = {
  active: "Activo",
  inactive: "Inactivo",
  suspended: "Suspendido",
  terminated: "Terminado",
};

const eventTypeLabels = {
  hire: "Ingreso",
  salary_change: "Cambio salarial",
  promotion: "Promocion",
  department_change: "Cambio de depto.",
  branch_change: "Cambio de sucursal",
  warning: "Advertencia",
  suspension: "Suspension",
  incident: "Incidencia",
  vale_created: "Vale creado",
  termination: "Terminación laboral",
  rehire: "Reingreso",
  contract_generated: "Contrato generado",
  contract_voided: "Contrato anulado",
  vacation_granted: "Vacaciones otorgadas",
};

const eventColors = {
  hire: "bg-emerald-100 text-emerald-800",
  salary_change: "bg-blue-100 text-blue-800",
  promotion: "bg-violet-100 text-violet-800",
  department_change: "bg-indigo-100 text-indigo-800",
  branch_change: "bg-cyan-100 text-cyan-800",
  warning: "bg-amber-100 text-amber-800",
  suspension: "bg-orange-100 text-orange-800",
  termination: "bg-red-100 text-red-800",
  rehire: "bg-emerald-100 text-emerald-800",
  contract_generated: "bg-indigo-100 text-indigo-800",
  contract_voided: "bg-zinc-100 text-zinc-700",
  vacation_granted: "bg-sky-100 text-sky-800",
};

const TERMINATION_REASONS = [
  { code: "despido_con_responsabilidad", label: "Despido con responsabilidad patronal" },
  { code: "despido_sin_responsabilidad", label: "Despido sin responsabilidad patronal" },
  { code: "renuncia", label: "Renuncia" },
  { code: "mutuo_acuerdo", label: "Mutuo acuerdo" },
  { code: "vencimiento_contrato", label: "Vencimiento de contrato" },
  { code: "fallecimiento", label: "Fallecimiento" },
];

/**
 * Antigüedad laboral entre `hire_date` (YYYY-MM-DD) y `end` (YYYY-MM-DD o hoy).
 * Devuelve { totalDays, years, months, days } o null si la fecha es inválida.
 */
function computeSeniority(hireDate, endDate) {
  if (!hireDate) return null;
  const start = new Date(hireDate + "T00:00:00");
  const end = endDate ? new Date(endDate + "T00:00:00") : new Date();
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;

  const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));

  // Diferencia calendario yy/mm/dd (con préstamo de meses/años, igual que `dateadd`-style)
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();
  if (days < 0) {
    // Días del mes anterior al actual del end
    const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
    days += prevMonth;
    months -= 1;
  }
  if (months < 0) {
    months += 12;
    years -= 1;
  }
  return { totalDays, years, months, days };
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start py-3 border-b border-zinc-100 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-400 w-44 shrink-0 pt-0.5">
        {label}
      </span>
      <span className="text-sm text-zinc-900">{value || "\u2014"}</span>
    </div>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [catalogs, setCatalogs] = useState({});
  const [payrollCfg, setPayrollCfg] = useState({ overtime_multiplier: 1.5 });
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Termination & Rehire state
  const [termOpen, setTermOpen] = useState(false);
  const [termForm, setTermForm] = useState({
    termination_date: new Date().toISOString().slice(0, 10),
    termination_reason: "",
    termination_notes: "",
  });
  const [termSubmitting, setTermSubmitting] = useState(false);

  const [rehireOpen, setRehireOpen] = useState(false);
  const [rehireForm, setRehireForm] = useState({
    rehire_date: new Date().toISOString().slice(0, 10),
    notes: "",
    generate_contract: "yes",   // yes | no
    keep_data: "keep",          // keep | modify
  });
  const [rehireSubmitting, setRehireSubmitting] = useState(false);

  // Biometrics state
  const [enrolling, setEnrolling] = useState(false);
  const [faceModelsLoaded, setFaceModelsLoaded] = useState(false);
  const [capturedDescriptors, setCapturedDescriptors] = useState([]);
  const [capturedPhotoBlob, setCapturedPhotoBlob] = useState(null);
  const [enrollStep, setEnrollStep] = useState(0); // 0,1,2
  const [faceDetected, setFaceDetected] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const enrollVideoRef = useRef(null);
  const enrollStreamRef = useRef(null);
  const detectionLoopRef = useRef(null);

  const ENROLL_STEPS = [
    { instruction: "Mire directamente a la camara", icon: "center" },
    { instruction: "Gire levemente a la izquierda", icon: "left" },
    { instruction: "Gire levemente a la derecha", icon: "right" },
  ];

  // Start camera + face detection loop when enrolling
  useEffect(() => {
    if (!enrolling) return;
    let cancelled = false;
    const startCamera = async () => {
      // 1) Abrir la cámara PRIMERO (dispara el permiso y muestra el video,
      //    aunque los modelos tarden o fallen).
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error("Este navegador no permite acceso a la cámara o la conexión no es segura (HTTPS).");
        setEnrolling(false);
        return;
      }
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640, min: 320 },
            height: { ideal: 480, min: 240 },
            facingMode: { ideal: "user" },
          },
        });
      } catch (err) {
        if (!cancelled) {
          const name = err?.name || "";
          let msg = "No se pudo acceder a la cámara. Verifique los permisos del navegador.";
          if (name === "NotAllowedError" || name === "SecurityError")
            msg = "Permiso de cámara denegado. Habilítelo en el candado de la barra de direcciones y recargue la página.";
          else if (name === "NotReadableError" || name === "TrackStartError")
            msg = "La cámara está en uso por otra aplicación o pestaña (¿el Kiosko está abierto?). Ciérrela e intente de nuevo.";
          else if (name === "NotFoundError" || name === "OverconstrainedError")
            msg = "No se encontró ninguna cámara compatible en este equipo.";
          console.error("Enroll camera error:", err);
          toast.error(msg);
          setEnrolling(false);
        }
        return;
      }
      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
      enrollStreamRef.current = stream;
      if (enrollVideoRef.current) {
        enrollVideoRef.current.srcObject = stream;
      }

      // 2) Cargar modelos de reconocimiento (local con reintentos, fallback a CDN).
      try {
        if (!faceModelsLoaded) {
          await loadFaceModels();
          if (!cancelled) setFaceModelsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Enroll models error:", err);
          toast.error("No se pudieron cargar los modelos de reconocimiento facial. Recargue la página e intente de nuevo.");
        }
        return;
      }

      // 3) Loop de detección para feedback en tiempo real.
      detectionLoopRef.current = setInterval(async () => {
        if (!enrollVideoRef.current || cancelled) return;
        try {
          const det = await faceapi.detectSingleFace(
            enrollVideoRef.current,
            new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.5 })
          );
          if (!cancelled) {
            if (det) {
              // Validate face is centered and properly sized within the oval
              const vw = enrollVideoRef.current.videoWidth || 480;
              const vh = enrollVideoRef.current.videoHeight || 360;
              const box = det.box;
              const faceCenterX = box.x + box.width / 2;
              const faceCenterY = box.y + box.height / 2;
              const videoCenterX = vw / 2;
              const videoCenterY = vh / 2;

              // Face must be centered (within 20% of center)
              const offsetX = Math.abs(faceCenterX - videoCenterX) / vw;
              const offsetY = Math.abs(faceCenterY - videoCenterY) / vh;
              const isCentered = offsetX < 0.2 && offsetY < 0.2;

              // Face must be reasonable size (between 15% and 60% of frame)
              const faceRatio = (box.width * box.height) / (vw * vh);
              const isGoodSize = faceRatio > 0.04 && faceRatio < 0.5;

              setFaceDetected(isCentered && isGoodSize);
            } else {
              setFaceDetected(false);
            }
          }
        } catch { /* ignore */ }
      }, 500);
    };
    startCamera();
    return () => {
      cancelled = true;
      if (detectionLoopRef.current) clearInterval(detectionLoopRef.current);
      if (enrollStreamRef.current) {
        enrollStreamRef.current.getTracks().forEach(t => t.stop());
        enrollStreamRef.current = null;
      }
    };
  }, [enrolling, faceModelsLoaded]);

  const snapshotFrontalPhoto = () => {
    try {
      const video = enrollVideoRef.current;
      if (!video) return null;
      const canvas = document.createElement("canvas");
      const vw = video.videoWidth || 480;
      const vh = video.videoHeight || 360;
      canvas.width = vw;
      canvas.height = vh;
      const ctx = canvas.getContext("2d");
      // Un-mirror (video is CSS-flipped for selfie UX — save the un-flipped frame)
      ctx.translate(vw, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, vw, vh);
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
      });
    } catch {
      return null;
    }
  };

  const captureStep = async () => {
    if (!enrollVideoRef.current || !faceModelsLoaded || !faceDetected) return;
    try {
      const detection = await faceapi
        .detectSingleFace(enrollVideoRef.current, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.5 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();
      if (detection) {
        // Double-check position at capture time
        const vw = enrollVideoRef.current.videoWidth || 480;
        const vh = enrollVideoRef.current.videoHeight || 360;
        const box = detection.detection.box;
        const offsetX = Math.abs((box.x + box.width / 2) - vw / 2) / vw;
        const offsetY = Math.abs((box.y + box.height / 2) - vh / 2) / vh;
        const faceRatio = (box.width * box.height) / (vw * vh);

        if (offsetX >= 0.2 || offsetY >= 0.2) {
          toast.error("Rostro fuera de posicion. Centre su cara dentro del ovalo.");
          return;
        }
        if (faceRatio < 0.04) {
          toast.error("Muy lejos de la camara. Acerquese un poco.");
          return;
        }
        if (faceRatio > 0.5) {
          toast.error("Muy cerca de la camara. Alejese un poco.");
          return;
        }

        setCapturedDescriptors(prev => [...prev, Array.from(detection.descriptor)]);

        // Save the frontal frame (step 0) as a profile-photo candidate
        if (enrollStep === 0) {
          try {
            const blob = await snapshotFrontalPhoto();
            if (blob) setCapturedPhotoBlob(blob);
          } catch { /* photo optional */ }
        }

        const nextStep = enrollStep + 1;
        if (nextStep < 3) {
          setEnrollStep(nextStep);
          toast.success(`Muestra ${nextStep} de 3 capturada`);
        } else {
          toast.success("3 muestras capturadas. Listo para guardar.");
        }
      } else {
        toast.error("No se detecto rostro. Centre su cara dentro del ovalo.");
      }
    } catch {
      toast.error("Error al detectar rostro");
    }
  };

  const saveEnrollment = async () => {
    try {
      await api.post("/kiosk/enroll-face", { employee_id: id, descriptors: capturedDescriptors });

      // Also upload the frontal snapshot as the profile photo (best-effort, non-blocking).
      if (capturedPhotoBlob) {
        try {
          const file = new File([capturedPhotoBlob], `profile-${id}.jpg`, { type: "image/jpeg" });
          const rec = await uploadFile(file, { category: "employee-photo", entityId: id });
          await api.put(`/employees/${id}`, { photo_path: rec.storage_path });
          toast.success("Rostro enrolado y foto de perfil actualizada");
        } catch {
          toast.success("Rostro enrolado (foto de perfil no se pudo guardar)");
        }
      } else {
        toast.success("Rostro enrolado exitosamente");
      }

      setEnrolling(false);
      setCapturedDescriptors([]);
      setCapturedPhotoBlob(null);
      setEnrollStep(0);
      setFaceDetected(false);
      fetchAll();
    } catch { toast.error("Error al enrolar"); }
  };

  const cancelEnrollment = () => {
    setEnrolling(false);
    setCapturedDescriptors([]);
    setCapturedPhotoBlob(null);
    setEnrollStep(0);
    setFaceDetected(false);
  };

  const fetchAll = () => {
    Promise.all([
      api.get(`/employees/${id}`),
      api.get(`/employees/${id}/timeline`),
      api.get("/branches"),
      api.get("/departments"),
      api.get("/occupations"),
      api.get("/schedules"),
      api.get("/companies").catch(() => ({ data: [] })),
      api.get("/catalogs").catch(() => ({ data: {} })),
      api.get("/payroll-config").catch(() => ({ data: {} })),
    ])
      .then(([empRes, tlRes, brRes, deptRes, occRes, schRes, coRes, catRes, pcRes]) => {
        setEmployee(empRes.data);
        setTimeline(tlRes.data);
        setBranches(brRes.data);
        setDepartments(deptRes.data);
        setOccupations(occRes.data);
        setSchedules(schRes.data);
        setCompanies(coRes.data);
        setCatalogs(catRes.data);
        setPayrollCfg(pcRes.data || {});
      })
      .catch(() => toast.error("Error al cargar datos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBranchName = (bid) => branches.find((b) => b.id === bid)?.name || "\u2014";
  const getDeptName = (did) => departments.find((d) => d.id === did)?.name || "\u2014";
  const getOccName = (oid) => occupations.find((o) => o.id === oid)?.name || "\u2014";
  const getCompanyName = (cid) => {
    const c = companies.find((c) => c.id === cid);
    return c ? `${c.legal_name}${c.is_primary ? " ★" : ""}` : "\u2014";
  };
  const getCatalogLabel = (name, code) => {
    const list = catalogs[name] || [];
    return list.find((x) => x.code === code)?.label || code || "\u2014";
  };
  const getWorkDaysText = (arr) => {
    if (!arr || !arr.length) return "\u2014";
    const dict = Object.fromEntries((catalogs["week-days"] || []).map((d) => [d.code, d.label]));
    return arr.map((d) => dict[d] || d).join(", ");
  };
  const getScheduleName = (sid) => {
    const s = schedules.find((s) => s.id === sid);
    return s ? `${s.name} (${s.start_time}-${s.end_time})` : "\u2014";
  };
  const getDayScheduleRows = () => {
    const ds = employee.day_schedules || {};
    const order = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
    const dict = Object.fromEntries((catalogs["week-days"] || []).map((d) => [d.code, d.label]));
    return order
      .filter((c) => ds[c])
      .map((c) => ({ label: dict[c] || c, value: `${ds[c].start || "\u2014"} \u2013 ${ds[c].end || "\u2014"}` }));
  };
  const getBreakText = () => {
    const bs = employee.break_schedule || {};
    const labels = { breakfast: "Desayuno", lunch: "Almuerzo", refrigerio: "Refrigerio" };
    const rows = Object.entries(labels)
      .map(([k, l]) => {
        const m = bs[k] && bs[k].minutes;
        return m ? `${l}: ${m} min` : null;
      })
      .filter(Boolean);
    return rows.length ? rows.join(" · ") : "\u2014";
  };
  const getDeductionsText = () => {
    const map = { VALES: "Vales / Anticipos", GARANTIAS_SOCIALES: "Garantías sociales (CCSS, INS)", SUSPENCIONES: "Suspensiones disciplinarias", OTRO: "Otras" };
    const list = (employee.authorized_deductions || []).map((d) => map[d] || d);
    return list.length ? list.join(", ") : "\u2014";
  };
  const fixationLabel = (v) => {
    if (v === "monthly") return "Mensual";
    if (v === "daily") return "Diaria";
    return getCatalogLabel("salary-fixations", v);
  };

  const [editSection, setEditSection] = useState(null);

  const openEdit = (section = "identity") => {
    if (!employee) return;
    // Compatibilidad: trabajadores antiguos solo tienen full_name (sin campos
    // separados). Si first_name/last_name están vacíos, derivamos del full_name.
    const splitFullName = (fn) => {
      const parts = (fn || "").trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return { first_name: "", last_name_1: "", last_name_2: "" };
      if (parts.length === 1) return { first_name: parts[0], last_name_1: "", last_name_2: "" };
      if (parts.length === 2) return { first_name: parts[0], last_name_1: parts[1], last_name_2: "" };
      return {
        first_name: parts.slice(0, parts.length - 2).join(" "),
        last_name_1: parts[parts.length - 2],
        last_name_2: parts[parts.length - 1],
      };
    };
    const nameParts =
      !employee.first_name && !employee.last_name_1 && employee.full_name
        ? splitFullName(employee.full_name)
        : {
            first_name: employee.first_name || "",
            last_name_1: employee.last_name_1 || "",
            last_name_2: employee.last_name_2 || "",
          };
    setEditSection(section);
    setEditForm({
      // identity
      first_name: nameParts.first_name,
      last_name_1: nameParts.last_name_1,
      last_name_2: nameParts.last_name_2,
      full_name: employee.full_name || "",
      worker_type: employee.worker_type || "permanent",
      id_type: employee.id_type || "CN",
      national_id: employee.national_id || "",
      birth_date: employee.birth_date || "",
      birth_place: employee.birth_place || "",
      nationality: employee.nationality || "CR",
      gender: employee.gender || "",
      marital_status: employee.marital_status || "",
      education_level: employee.education_level || "",
      // contact
      phone: employee.phone || "",
      email: employee.email || "",
      address: employee.address || "",
      emergency_contact_name: employee.emergency_contact_name || "",
      emergency_contact_relationship: employee.emergency_contact_relationship || "",
      emergency_contact_phone: employee.emergency_contact_phone || "",
      // contract
      hire_date: employee.hire_date || "",
      company_id: employee.company_id || "",
      primary_branch_id: employee.primary_branch_id || "",
      department_id: employee.department_id || "",
      occupation_id: employee.occupation_id || "",
      specific_labor: employee.specific_labor || "",
      contract_type: employee.contract_type || "CTI",
      contract_reason: employee.contract_reason || "",
      contract_end_date: employee.contract_end_date || "",
      status: employee.status || "active",
      // conditions
      journey_type: employee.journey_type || "JOD",
      journey_mode: employee.journey_mode || "continua",
      social_guarantees: employee.social_guarantees || { ccss: true, ins: true },
      work_schedule_type: employee.work_schedule_type || "tiempo_completo",
      schedule_id: employee.schedule_id || "",
      start_time: employee.start_time || "",
      end_time: employee.end_time || "",
      work_days: employee.work_days || [],
      day_off: employee.day_off || "",
      days_off: employee.days_off || [],
      day_schedules: employee.day_schedules || {},
      schedule_accepted: employee.schedule_accepted || false,
      break_schedule: employee.break_schedule || { breakfast: { minutes: 15 }, lunch: { minutes: 30 }, refrigerio: { minutes: 15 } },
      // payment
      base_salary: employee.base_salary || 0,
      salary_fixation: employee.salary_fixation || "ordinaria",
      payment_method: employee.payment_method || "EFE",
      payment_periodicity: employee.payment_periodicity || "QUINCENA",
      bank_account: employee.bank_account || "",
      authorized_deductions: employee.authorized_deductions || ["GARANTIAS_SOCIALES"],
      other_deductions: employee.other_deductions || [],
    });
    setEditOpen(true);
  };

  const SECTION_FIELDS = {
    identity: [
      "first_name", "last_name_1", "last_name_2", "worker_type",
      "id_type", "national_id", "birth_date", "birth_place", "nationality",
      "gender", "marital_status", "education_level",
    ],
    contact: [
      "phone", "email", "address",
      "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_phone",
    ],
    contract: [
      "hire_date", "company_id", "primary_branch_id", "department_id",
      "occupation_id", "contract_type", "contract_reason", "contract_end_date", "status",
    ],
    conditions: [
      "journey_type", "journey_mode", "social_guarantees",
      "work_schedule_type", "schedule_id", "start_time", "end_time",
      "work_days", "day_off", "days_off", "day_schedules", "schedule_accepted", "break_schedule",
    ],
    payment: [
      "base_salary",
      "salary_fixation", "payment_method", "payment_periodicity", "bank_account",
      "authorized_deductions", "other_deductions",
    ],
  };

  const handleUpdate = async () => {
    const fields = SECTION_FIELDS[editSection] || Object.keys(editForm);
    const payload = {};
    for (const k of fields) payload[k] = editForm[k];

    // Compose full_name when editing identity
    if (editSection === "identity") {
      const parts = [payload.first_name, payload.last_name_1, payload.last_name_2]
        .filter(Boolean)
        .map((s) => s.trim());
      if (parts.length) payload.full_name = parts.join(" ");
    }
    // Derive general start/end and days off from per-day schedule
    if (editSection === "conditions") {
      const allDays = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
      const ds = payload.day_schedules || {};
      const wd = payload.work_days || [];
      if (wd.length === 0) { toast.error("Seleccione al menos un día laboral"); return; }
      const missing = wd.filter((d) => !ds[d]?.start || !ds[d]?.end);
      if (missing.length) {
        toast.error("Defina la hora de entrada y salida en TODOS los días laborales");
        return;
      }
      const firstDay = wd[0];
      if (firstDay && ds[firstDay]) {
        payload.start_time = ds[firstDay].start || payload.start_time;
        payload.end_time = ds[firstDay].end || payload.end_time;
      }
      const daysOff = allDays.filter((d) => !(payload.work_days || []).includes(d));
      payload.days_off = daysOff;
      payload.day_off = daysOff[0] || "";
    }
    // Fijación salarial se hereda del tipo de salario de la ocupación (Mensual/Diaria)
    if (editSection === "payment") {
      const occ = occupations.find((o) => o.id === employee.occupation_id);
      if (occ?.salary_type) payload.salary_fixation = occ.salary_type;
    }
    // Normalize empty IDs
    ["company_id", "department_id", "occupation_id", "schedule_id"].forEach((k) => {
      if (payload[k] === "none" || payload[k] === "") payload[k] = null;
    });
    // Cast numbers
    ["base_salary", "min_salary", "payment_amount_tm"].forEach((k) => {
      if (k in payload) payload[k] = Number(payload[k]) || 0;
    });

    try {
      await api.put(`/employees/${id}`, payload);
      toast.success("Datos actualizados");
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    }
  };

  const openTerminate = () => {
    setTermForm({
      termination_date: new Date().toISOString().slice(0, 10),
      termination_reason: "",
      termination_notes: "",
    });
    setTermOpen(true);
  };

  const submitTermination = async () => {
    if (!termForm.termination_date) {
      toast.error("Seleccione la fecha de terminación");
      return;
    }
    if (!termForm.termination_reason) {
      toast.error("Seleccione la razón de terminación");
      return;
    }
    setTermSubmitting(true);
    try {
      await api.post(`/employees/${id}/terminate`, termForm);
      toast.success("Relación laboral terminada");
      setTermOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al terminar la relación");
    } finally {
      setTermSubmitting(false);
    }
  };

  const openRehire = () => {
    setRehireForm({
      rehire_date: new Date().toISOString().slice(0, 10),
      notes: "",
      generate_contract: "yes",
      keep_data: "keep",
    });
    setRehireOpen(true);
  };

  const submitRehire = async () => {
    if (!rehireForm.rehire_date) {
      toast.error("Seleccione la fecha de reingreso");
      return;
    }
    setRehireSubmitting(true);
    try {
      await api.post(`/employees/${id}/rehire`, {
        rehire_date: rehireForm.rehire_date,
        notes: rehireForm.notes,
      });

      if (rehireForm.generate_contract === "yes" && rehireForm.keep_data === "keep") {
        try {
          await api.post(`/contracts/generate`, { employee_id: id });
          toast.success("Empleado reingresado y nuevo contrato generado");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Reingreso completado, pero no se pudo generar contrato");
        }
      } else if (rehireForm.generate_contract === "yes" && rehireForm.keep_data === "modify") {
        toast.success("Empleado reingresado. Modifique los datos y use «Generar Contrato» en la sección Contratos.");
      } else {
        toast.success("Empleado reingresado");
      }

      setRehireOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al reingresar al empleado");
    } finally {
      setRehireSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-16 text-zinc-400">
        <p>Empleado no encontrado</p>
        <Button variant="link" onClick={() => navigate("/empleados")} className="mt-2">
          Volver a empleados
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="employee-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/empleados")}
        className="mb-4 text-zinc-500 hover:text-zinc-900 -ml-3"
        data-testid="back-to-employees"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Empleados
      </Button>

      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          <EmployeePhoto
            employee={employee}
            onUpdated={(updated) => setEmployee((prev) => ({ ...prev, ...updated }))}
          />
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
                {employee.full_name}
              </h1>
              <Badge
                variant="outline"
                className={`font-medium ${statusColors[employee.status] || ""}`}
              >
                {statusLabels[employee.status] || employee.status}
              </Badge>
              {employee.worker_type === "occasional" && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-medium">
                  Ocasional
                </Badge>
              )}
            </div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 mt-1">
              Expediente digital
            </p>
            <p className="text-sm text-zinc-500 mt-0.5 font-mono">
              {employee.employee_code}
            </p>
            {employee.status === "terminated" && employee.termination_date && (
              <p className="text-xs text-red-700 mt-1.5">
                Terminado el {formatDate(employee.termination_date)}
                {employee.termination_reason && (
                  <> · {TERMINATION_REASONS.find(r => r.code === employee.termination_reason)?.label || employee.termination_reason}</>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-end sm:items-start gap-2">
          <div className="flex flex-col items-end gap-1.5">
            {employee.status === "terminated" ? (
              <Button
                onClick={openRehire}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="rehire-employee-button"
              >
                <UserCheck className="w-4 h-4 mr-2" /> Reingreso
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={openTerminate}
                className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="terminate-employee-button"
              >
                <UserX className="w-4 h-4 mr-2" /> Terminar relación
              </Button>
            )}
            {(() => {
              const sen = computeSeniority(
                employee.hire_date,
                employee.status === "terminated" ? employee.termination_date : null,
              );
              if (!sen) return null;
              const dayLabel = sen.totalDays === 1 ? "día" : "días";
              return (
                <div
                  className="text-right pr-1"
                  data-testid="employee-seniority"
                  title={`Desde ${formatDate(employee.hire_date)}${employee.status === "terminated" && employee.termination_date ? ` hasta ${formatDate(employee.termination_date)}` : ""}`}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400 leading-tight">
                    Antigüedad laboral
                  </p>
                  <p className="text-sm font-mono text-zinc-800 leading-tight mt-0.5">
                    {sen.totalDays.toLocaleString("es-CR")} {dayLabel}{" "}
                    <span className="text-zinc-500">
                      ({String(sen.years).padStart(2, "0")}y,{" "}
                      {String(sen.months).padStart(2, "0")}m,{" "}
                      {String(sen.days).padStart(2, "0")}d)
                    </span>
                  </p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="border-b border-zinc-200 bg-transparent w-full justify-start rounded-none p-0 h-auto gap-0">
          <TabsTrigger
            value="info"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-info"
          >
            <User className="w-4 h-4 mr-2" /> Información
          </TabsTrigger>
          <TabsTrigger
            value="documentos"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-documentos"
          >
            <FileText className="w-4 h-4 mr-2" /> Documentos
          </TabsTrigger>
          <TabsTrigger
            value="timeline"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-timeline"
          >
            <Clock className="w-4 h-4 mr-2" /> Línea de tiempo
          </TabsTrigger>
          <TabsTrigger
            value="biometria"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-biometria"
          >
            <ScanFace className="w-4 h-4 mr-2" /> Biometría
          </TabsTrigger>
          <TabsTrigger
            value="vales"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-vales"
          >
            <Wallet className="w-4 h-4 mr-2" /> Vales
          </TabsTrigger>
          <TabsTrigger
            value="vacaciones"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-vacaciones"
          >
            <Plane className="w-4 h-4 mr-2" /> Vacaciones
          </TabsTrigger>
          <TabsTrigger
            value="salarios"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
            data-testid="tab-salarios"
          >
            <Coins className="w-4 h-4 mr-2" /> Salarios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6 space-y-4">
          {/* Identificación */}
          <SectionCard
            title="Identificación"
            onEdit={() => openEdit("identity")}
            testid="edit-identity-button"
          >
            <InfoRow
              label="Nombre completo"
              value={
                [employee.first_name, employee.last_name_1, employee.last_name_2]
                  .filter(Boolean)
                  .join(" ") || employee.full_name
              }
            />
            <InfoRow
              label="Tipo de trabajador"
              value={employee.worker_type === "occasional" ? "Ocasional" : "Permanente"}
            />
            <InfoRow
              label="Tipo de identificación"
              value={getCatalogLabel("id-types", employee.id_type)}
            />
            <InfoRow label="Número de identificación" value={employee.national_id} />
            <InfoRow label="Fecha de nacimiento" value={employee.birth_date} />
            <InfoRow label="Lugar de nacimiento" value={employee.birth_place} />
            <InfoRow label="Nacionalidad" value={getCatalogLabel("nationalities", employee.nationality)} />
            <InfoRow label="Sexo" value={getCatalogLabel("genders", employee.gender)} />
            <InfoRow label="Estado civil" value={getCatalogLabel("marital-statuses", employee.marital_status)} />
            <InfoRow label="Escolaridad" value={getCatalogLabel("education-levels", employee.education_level)} />
          </SectionCard>

          {/* Contacto */}
          <SectionCard
            title="Contacto"
            onEdit={() => openEdit("contact")}
            testid="edit-contact-button"
          >
            <InfoRow label="Teléfono" value={employee.phone} />
            <InfoRow label="Correo electrónico" value={employee.email} />
            <InfoRow label="Dirección / residencia" value={employee.address} />
            <Separator className="my-3" />
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500 mb-2">
              Contacto de emergencia
            </h4>
            <InfoRow label="Nombre" value={employee.emergency_contact_name} />
            <InfoRow
              label="Parentesco"
              value={getCatalogLabel("relationships", employee.emergency_contact_relationship)}
            />
            <InfoRow label="Teléfono" value={employee.emergency_contact_phone} />
          </SectionCard>

          {/* Naturaleza del contrato */}
          <SectionCard
            title="Naturaleza del contrato"
            onEdit={() => openEdit("contract")}
            testid="edit-contract-button"
          >
            <InfoRow label="Código" value={employee.employee_code} />
            <InfoRow label="Fecha de ingreso" value={formatDate(employee.hire_date)} />
            <InfoRow label="Empresa" value={getCompanyName(employee.company_id)} />
            <InfoRow label="Lugar de trabajo / Sucursal" value={getBranchName(employee.primary_branch_id)} />
            <InfoRow label="Departamento" value={getDeptName(employee.department_id)} />
            <InfoRow label="Ocupación" value={getOccName(employee.occupation_id)} />
            <InfoRow label="Tipo de contrato" value={getCatalogLabel("contract-types", employee.contract_type)} />
            {employee.contract_type && employee.contract_type !== "CTI" && (
              <>
                <InfoRow label="Motivo / obra del plazo" value={employee.contract_reason} />
                <InfoRow label="Fecha de finalización" value={formatDate(employee.contract_end_date)} />
              </>
            )}
          </SectionCard>

          {/* Condiciones laborales */}
          <SectionCard
            title="Condiciones laborales"
            onEdit={() => openEdit("conditions")}
            testid="edit-conditions-button"
          >
            <InfoRow label="Jornada laboral" value={getCatalogLabel("journey-types", employee.journey_type)} />
            <InfoRow label="Manera de jornada" value={getCatalogLabel("journey-modes", employee.journey_mode)} />
            <InfoRow
              label="Garantías sociales"
              value={
                employee.social_guarantees
                  ? [
                      employee.social_guarantees.ccss ? "CCSS" : null,
                      employee.social_guarantees.ins ? "INS" : null,
                    ]
                      .filter(Boolean)
                      .join(" + ") || "Ninguna"
                  : "\u2014"
              }
            />
            <InfoRow label="Horario de trabajo" value={getCatalogLabel("work-schedule-types", employee.work_schedule_type)} />
            <InfoRow label="Jornada configurada" value={getScheduleName(employee.schedule_id)} />
            {getDayScheduleRows().length > 0 ? (
              getDayScheduleRows().map((r) => (
                <InfoRow key={r.label} label={`Horario · ${r.label}`} value={r.value} />
              ))
            ) : (
              <InfoRow
                label="Horario"
                value={
                  employee.start_time && employee.end_time
                    ? `${employee.start_time} – ${employee.end_time}`
                    : "\u2014"
                }
              />
            )}
            <InfoRow
              label="Día(s) libre(s)"
              value={
                (employee.days_off && employee.days_off.length
                  ? getWorkDaysText(employee.days_off)
                  : getCatalogLabel("week-days", employee.day_off))
              }
            />
            <InfoRow label="Tiempos de descanso" value={getBreakText()} />
            <InfoRow
              label="Aceptación de jornada"
              value={employee.schedule_accepted ? "Aceptada por el trabajador" : "Pendiente / no registrada"}
            />
          </SectionCard>

          {/* Información de pago */}
          <SectionCard
            title="Información de pago"
            onEdit={() => openEdit("payment")}
            testid="edit-payment-button"
          >
            <InfoRow
              label="Salario base"
              value={
                employee.base_salary
                  ? `\u20A1 ${Number(employee.base_salary).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "\u2014"
              }
            />
            <InfoRow label="Fijación salarial" value={fixationLabel(employee.salary_fixation)} />
            <InfoRow label="Forma de pago" value={getCatalogLabel("payment-methods", employee.payment_method)} />
            <InfoRow label="Periodicidad" value={getCatalogLabel("payment-periodicities", employee.payment_periodicity)} />
            <InfoRow label="Cuenta bancaria" value={employee.bank_account} />
            <InfoRow label="Deducciones autorizadas" value={getDeductionsText()} />
            {(employee.other_deductions || []).length > 0 && (
              <InfoRow label="Otras deducciones" value={(employee.other_deductions || []).join(", ")} />
            )}
            {Number(employee.base_salary) > 0 && (() => {
              const occ = occupations.find((o) => o.id === employee.occupation_id);
              const salaryType = occ?.salary_type || "monthly";
              return (
                <div className="mt-4 space-y-3">
                  <SalaryBreakdown amount={employee.base_salary} salaryType={salaryType} cfg={payrollCfg} testid="detail-salary-breakdown" />
                  <OvertimeBreakdown amount={employee.base_salary} salaryType={salaryType} cfg={payrollCfg} daySchedules={employee.day_schedules} journeyType={employee.journey_type} testid="detail-overtime-breakdown" />
                  <MonthlyPayEstimate amount={employee.base_salary} salaryType={salaryType} cfg={payrollCfg} daySchedules={employee.day_schedules} journeyType={employee.journey_type} testid="detail-monthly-pay-estimate" />
                </div>
              );
            })()}
          </SectionCard>
        </TabsContent>

        <TabsContent value="documentos" className="mt-6">
          <EmployeeDocumentsPanel
            employeeId={employee.id}
            documentTypes={catalogs["document-types"] || []}
          />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <CardContent className="p-6">
              {timeline.length === 0 ? (
                <p className="text-center py-10 text-zinc-400 text-sm">
                  No hay eventos registrados
                </p>
              ) : (
                <div className="space-y-0">
                  {timeline.map((evt, i) => (
                    <div
                      key={evt.id || i}
                      className="flex gap-4 items-start py-3 border-b border-zinc-100 last:border-0"
                    >
                      <div className="w-2 h-2 rounded-full bg-zinc-300 mt-2 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-zinc-900">
                            {evt.title}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-medium ${eventColors[evt.event_type] || "bg-zinc-100 text-zinc-700"}`}
                          >
                            {eventTypeLabels[evt.event_type] || evt.event_type}
                          </Badge>
                        </div>
                        {evt.description && (
                          <p className="text-sm text-zinc-500 mt-0.5">
                            {evt.description}
                          </p>
                        )}
                        <p className="text-xs text-zinc-400 mt-1">
                          {formatDate(evt.event_date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="biometria" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Face Enrollment */}
            <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ScanFace className="w-5 h-5 text-zinc-700" />
                  <h3 className="text-sm font-semibold text-zinc-900">Reconocimiento facial</h3>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="outline" className={employee.face_enrolled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                    {employee.face_enrolled ? "Enrolado" : "No enrolado"}
                  </Badge>
                  {capturedDescriptors.length > 0 && (
                    <span className="text-xs text-zinc-500">{capturedDescriptors.length} muestras capturadas</span>
                  )}
                </div>

                {enrolling ? (
                  <div>
                    {/* Step indicator */}
                    <div className="flex items-center gap-2 mb-3">
                      {ENROLL_STEPS.map((_, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                            i < capturedDescriptors.length
                              ? "bg-emerald-500 text-white"
                              : i === enrollStep
                                ? "bg-zinc-900 text-white"
                                : "bg-zinc-200 text-zinc-500"
                          }`}>
                            {i < capturedDescriptors.length ? "\u2713" : i + 1}
                          </div>
                          {i < 2 && <div className={`w-6 h-0.5 ${i < capturedDescriptors.length ? "bg-emerald-500" : "bg-zinc-200"}`} />}
                        </div>
                      ))}
                    </div>

                    {/* Instruction */}
                    {capturedDescriptors.length < 3 && (
                      <div className={`text-center py-2 px-3 rounded-lg mb-3 text-sm font-medium ${
                        faceDetected
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}>
                        {faceDetected
                          ? ENROLL_STEPS[enrollStep]?.instruction + " — Listo para capturar"
                          : "Ubique su rostro dentro del ovalo"
                        }
                      </div>
                    )}

                    {/* Camera with oval guide */}
                    <div className="relative rounded-xl overflow-hidden bg-zinc-900 mb-3">
                      <video ref={enrollVideoRef} autoPlay muted playsInline className="w-full aspect-[4/3] object-cover mirror" style={{ transform: "scaleX(-1)" }} />
                      {/* Oval face guide overlay */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className={`w-44 h-56 rounded-[50%] border-[3px] transition-colors duration-300 ${
                          faceDetected ? "border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.3)]" : "border-white/40"
                        }`} />
                      </div>
                      {/* Direction arrow */}
                      {capturedDescriptors.length < 3 && (
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                          <div className="bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2">
                            {enrollStep === 0 && <div className="w-3 h-3 rounded-full bg-white/80" />}
                            {enrollStep === 1 && <div className="text-white text-lg">&larr;</div>}
                            {enrollStep === 2 && <div className="text-white text-lg">&rarr;</div>}
                            <span className="text-white text-xs font-medium">
                              {enrollStep === 0 ? "De frente" : enrollStep === 1 ? "Izquierda" : "Derecha"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    {capturedDescriptors.length < 3 ? (
                      <Button
                        className={`w-full ${faceDetected ? "bg-zinc-900 text-white hover:bg-zinc-800" : "bg-zinc-300 text-zinc-500 cursor-not-allowed"}`}
                        disabled={!faceDetected}
                        onClick={captureStep}
                        data-testid="capture-face-button"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Capturar muestra {capturedDescriptors.length + 1} de 3
                      </Button>
                    ) : (
                      <Button
                        className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
                        onClick={saveEnrollment}
                        data-testid="save-face-button"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Guardar enrolamiento
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2 text-xs text-zinc-500"
                      onClick={cancelEnrollment}
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setEnrolling(true);
                      setCapturedDescriptors([]);
                      setEnrollStep(0);
                      setFaceDetected(false);
                    }}
                    data-testid="start-enroll-button"
                  >
                    <Camera className="w-4 h-4 mr-2" /> {employee.face_enrolled ? "Re-enrolar rostro" : "Enrolar rostro"}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* PIN Code */}
            <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <KeyRound className="w-5 h-5 text-zinc-700" />
                  <h3 className="text-sm font-semibold text-zinc-900">PIN de respaldo</h3>
                </div>
                <p className="text-sm text-zinc-500 mb-4">
                  PIN de 4-6 digitos para marcar asistencia cuando el reconocimiento facial no funcione.
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className={employee.pin_code ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                    {employee.pin_code ? "PIN asignado" : "Sin PIN"}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    maxLength={6}
                    value={pinValue}
                    onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ""))}
                    placeholder={employee.pin_code ? "Nuevo PIN" : "Asignar PIN (4-6 digitos)"}
                    className="flex-1"
                    data-testid="pin-code-input"
                  />
                  <Button
                    disabled={pinValue.length < 4}
                    onClick={async () => {
                      try {
                        await api.put(`/employees/${id}`, { pin_code: pinValue });
                        toast.success("PIN actualizado");
                        setPinValue("");
                        fetchAll();
                      } catch { toast.error("Error al guardar PIN"); }
                    }}
                    className="bg-zinc-900 text-white hover:bg-zinc-800"
                    data-testid="save-pin-button"
                  >
                    Guardar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="vales" className="mt-6">
          <EmployeeValesPanel employeeId={employee?.id} />
        </TabsContent>

        <TabsContent value="vacaciones" className="mt-6">
          {employee?.id && <EmployeeVacationsTab employeeId={employee.id} />}
        </TabsContent>

        <TabsContent value="salarios" className="mt-6">
          {employee?.id && <EmployeeSalariesTab employeeId={employee.id} />}
        </TabsContent>
      </Tabs>

      {/* Sectioned Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editSection === "identity" && "Editar — Identificación"}
              {editSection === "contact" && "Editar — Contacto"}
              {editSection === "contract" && "Editar — Naturaleza del contrato"}
              {editSection === "conditions" && "Editar — Condiciones laborales"}
              {editSection === "payment" && "Editar — Información de pago"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <SectionEditForm
              section={editSection}
              form={editForm}
              setForm={setEditForm}
              catalogs={catalogs}
              branches={branches}
              companies={companies}
              departments={departments}
              occupations={occupations}
              schedules={schedules}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-employee-cancel">
              Cancelar
            </Button>
            <Button
              onClick={handleUpdate}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="edit-employee-submit"
            >
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Termination Dialog */}
      <Dialog open={termOpen} onOpenChange={setTermOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <UserX className="w-5 h-5 text-red-600" />
              Terminar relación laboral
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
              Esta acción marcará al empleado como <b>terminado</b> y anulará los contratos vigentes.
              Los datos del expediente se conservan para historial legal.
            </div>

            <Fld label="Fecha de terminación">
              <DatePickerEs
                value={termForm.termination_date}
                onChange={(v) => setTermForm((f) => ({ ...f, termination_date: v }))}
                testId="termination-date-input"
              />
            </Fld>

            <Fld label="Razón de terminación">
              <Select
                value={termForm.termination_reason}
                onValueChange={(v) => setTermForm((f) => ({ ...f, termination_reason: v }))}
              >
                <SelectTrigger data-testid="termination-reason-select">
                  <SelectValue placeholder="Seleccione razón" />
                </SelectTrigger>
                <SelectContent>
                  {TERMINATION_REASONS.map((r) => (
                    <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Fld>

            <Fld label="Notas (opcional)">
              <textarea
                rows={3}
                value={termForm.termination_notes}
                onChange={(e) => setTermForm((f) => ({ ...f, termination_notes: e.target.value }))}
                className="w-full text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900"
                placeholder="Detalles adicionales sobre la terminación..."
                data-testid="termination-notes-input"
              />
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermOpen(false)} disabled={termSubmitting} data-testid="termination-cancel">
              Cancelar
            </Button>
            <Button
              onClick={submitTermination}
              disabled={termSubmitting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="termination-submit"
            >
              {termSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar terminación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rehire Dialog */}
      <Dialog open={rehireOpen} onOpenChange={setRehireOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              Reingreso del empleado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800">
              El empleado será reactivado. La nueva fecha de ingreso se usará para el próximo contrato.
            </div>

            <Fld label="Fecha de reingreso">
              <DatePickerEs
                value={rehireForm.rehire_date}
                onChange={(v) => setRehireForm((f) => ({ ...f, rehire_date: v }))}
                testId="rehire-date-input"
              />
            </Fld>

            <Fld label="¿Generar nuevo contrato laboral?">
              <Select
                value={rehireForm.generate_contract}
                onValueChange={(v) => setRehireForm((f) => ({ ...f, generate_contract: v }))}
              >
                <SelectTrigger data-testid="rehire-generate-contract-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Sí, generar contrato ahora</SelectItem>
                  <SelectItem value="no">No, solo reactivar</SelectItem>
                </SelectContent>
              </Select>
            </Fld>

            {rehireForm.generate_contract === "yes" && (
              <Fld label="¿Cómo proceder con los datos?">
                <Select
                  value={rehireForm.keep_data}
                  onValueChange={(v) => setRehireForm((f) => ({ ...f, keep_data: v }))}
                >
                  <SelectTrigger data-testid="rehire-keep-data-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep">Mantener todos los datos actuales</SelectItem>
                    <SelectItem value="modify">Quiero modificarlos primero</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
            )}

            <Fld label="Notas (opcional)">
              <textarea
                rows={2}
                value={rehireForm.notes}
                onChange={(e) => setRehireForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900"
                placeholder="Notas adicionales..."
                data-testid="rehire-notes-input"
              />
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRehireOpen(false)} disabled={rehireSubmitting} data-testid="rehire-cancel">
              Cancelar
            </Button>
            <Button
              onClick={submitRehire}
              disabled={rehireSubmitting}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="rehire-submit"
            >
              {rehireSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar reingreso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Employee Vales Panel — shown inside the employee detail page "Vales" tab
// ─────────────────────────────────────────────────────────────────────────
function EmployeeValesPanel({ employeeId }) {
  const [vales, setVales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (!employeeId) return;
    api.get("/vales", { params: { employee_id: employeeId } })
      .then((r) => setVales(r.data))
      .finally(() => setLoading(false));
  }, [employeeId]);

  const money = (n) =>
    `₡ ${Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const active = vales.filter((v) => v.status === "active");
  const totalBalance = active.reduce((sum, v) => sum + (v.remaining_amount || 0), 0);

  const statusStyle = {
    active: "bg-amber-50 text-amber-700 border-amber-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
  };
  const statusLabel = { active: "Activo", paid: "Saldado", cancelled: "Cancelado" };

  return (
    <div data-testid="employee-vales-panel">
      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] mb-4">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Saldo pendiente</p>
              <p className="text-2xl font-bold text-zinc-950 mt-1 font-mono">{money(totalBalance)}</p>
              <p className="text-[11px] text-zinc-500 mt-1">
                Este saldo se deducirá automáticamente en la próxima planilla.
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-zinc-400">{active.length} vale{active.length !== 1 ? "s" : ""} activo{active.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-zinc-400 text-center py-8">Cargando...</p>
      ) : vales.length === 0 ? (
        <Card className="bg-white border-zinc-200"><CardContent className="p-10 text-center text-zinc-400">
          <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Este empleado no tiene vales registrados</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {vales.map((v) => (
            <Card key={v.id} className="bg-white border-zinc-200" data-testid={`employee-vale-${v.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-zinc-900 font-mono">{money(v.amount)}</p>
                    <p className="text-[11px] text-zinc-500">
                      {formatDate(v.date_issued)} · {v.mode === "single" ? "Pago único" : `${v.installments_paid || 0}/${v.installments} cuotas · ${money(v.installment_amount)}/planilla`}
                    </p>
                    {v.reason && <p className="text-[11px] text-zinc-400 mt-0.5 italic">{v.reason}</p>}
                  </div>
                  <Badge variant="outline" className={`font-medium ${statusStyle[v.status]}`}>
                    {statusLabel[v.status]}
                  </Badge>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-zinc-100 text-xs">
                  <span className="text-zinc-500">Pagado: <span className="text-emerald-700 font-mono font-medium">{money(v.paid_amount)}</span></span>
                  <span className="text-zinc-500">Saldo: <span className="text-amber-700 font-mono font-medium">{money(v.remaining_amount)}</span></span>
                </div>
                {(v.payments || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-100">
                    <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Pagos aplicados</p>
                    <div className="space-y-0.5">
                      {v.payments.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                          <span className="text-zinc-600">{p.period_label}</span>
                          <span className="font-mono text-emerald-700">-{money(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Employee avatar with upload-on-click (stores in Object Storage)
// ─────────────────────────────────────────────────────────────────────────
function EmployeePhoto({ employee, onUpdated }) {
  const [uploading, setUploading] = useState(false);

  const initials = (employee.full_name || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no debe pasar de 5 MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const rec = await uploadFile(file, { category: "employee-photo", entityId: employee.id });
      const { data } = await api.put(`/employees/${employee.id}`, { photo_path: rec.storage_path });
      onUpdated?.(data);
      toast.success("Foto actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <label
      className="relative w-20 h-20 rounded-full overflow-hidden bg-zinc-100 border border-zinc-200 cursor-pointer group shrink-0"
      data-testid="employee-photo"
    >
      {employee.photo_path ? (
        <AuthImage
          path={employee.photo_path}
          alt={employee.full_name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-lg font-bold text-zinc-400">
          {initials || "—"}
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <Camera className="w-5 h-5 text-white" />
      </div>
      {uploading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-[10px] text-zinc-700">
          Subiendo…
        </div>
      )}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        disabled={uploading}
        data-testid="employee-photo-input"
      />
    </label>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Section card wrapper (title + pencil + content)
// ─────────────────────────────────────────────────────────────────────────
function SectionCard({ title, children, onEdit, testid }) {
  return (
    <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-900">
            {title}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-7 px-2 text-zinc-500 hover:text-zinc-900"
            data-testid={testid}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
          </Button>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Per-section edit forms (rendered inside the dialog)
// ─────────────────────────────────────────────────────────────────────────
function CatalogSel({ value, options, onChange, placeholder = "Selecciona", testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-zinc-700 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function SectionEditForm({ section, form, setForm, catalogs, branches, companies, departments, occupations, schedules }) {
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const C = (name) => catalogs[name] || [];

  if (section === "identity") {
    return (
      <>
        <div className="grid grid-cols-3 gap-3">
          <Fld label="Nombre">
            <Input value={form.first_name || ""} onChange={(e) => patch({ first_name: e.target.value })} data-testid="edit-first-name" />
          </Fld>
          <Fld label="Primer apellido">
            <Input value={form.last_name_1 || ""} onChange={(e) => patch({ last_name_1: e.target.value })} data-testid="edit-last-name-1" />
          </Fld>
          <Fld label="Segundo apellido">
            <Input value={form.last_name_2 || ""} onChange={(e) => patch({ last_name_2: e.target.value })} data-testid="edit-last-name-2" />
          </Fld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Tipo de trabajador">
            <CatalogSel value={form.worker_type} options={C("worker-types")} onChange={(v) => patch({ worker_type: v })} testid="edit-worker-type" />
          </Fld>
          <Fld label="Tipo de identificación">
            <CatalogSel value={form.id_type} options={C("id-types")} onChange={(v) => patch({ id_type: v })} testid="edit-id-type" />
          </Fld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Número de identificación">
            <Input value={form.national_id || ""} onChange={(e) => patch({ national_id: e.target.value })} data-testid="edit-national-id" />
          </Fld>
          <Fld label="Fecha de nacimiento">
            <DatePickerEs value={form.birth_date || ""} onChange={(iso) => patch({ birth_date: iso })} testId="edit-birth-date" />
          </Fld>
        </div>
        <Fld label="Lugar de nacimiento">
          <Input value={form.birth_place || ""} onChange={(e) => patch({ birth_place: e.target.value })} placeholder="Ej: Quepos, San José" data-testid="edit-birth-place" />
        </Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Nacionalidad">
            <CatalogSel value={form.nationality} options={C("nationalities")} onChange={(v) => patch({ nationality: v })} testid="edit-nationality" />
          </Fld>
          <Fld label="Sexo">
            <CatalogSel value={form.gender} options={C("genders")} onChange={(v) => patch({ gender: v })} testid="edit-gender" />
          </Fld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Estado civil">
            <CatalogSel value={form.marital_status} options={C("marital-statuses")} onChange={(v) => patch({ marital_status: v })} testid="edit-marital" />
          </Fld>
          <Fld label="Escolaridad">
            <CatalogSel value={form.education_level} options={C("education-levels")} onChange={(v) => patch({ education_level: v })} testid="edit-education" />
          </Fld>
        </div>
      </>
    );
  }

  if (section === "contact") {
    return (
      <>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Teléfono">
            <Input value={form.phone || ""} onChange={(e) => patch({ phone: e.target.value })} data-testid="edit-phone" />
          </Fld>
          <Fld label="Correo electrónico">
            <Input type="email" value={form.email || ""} onChange={(e) => patch({ email: e.target.value })} data-testid="edit-email" />
          </Fld>
        </div>
        <Fld label="Dirección / residencia">
          <Input value={form.address || ""} onChange={(e) => patch({ address: e.target.value })} data-testid="edit-address" />
        </Fld>
        <Separator className="my-2" />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Contacto de emergencia</p>
        <Fld label="Nombre">
          <Input value={form.emergency_contact_name || ""} onChange={(e) => patch({ emergency_contact_name: e.target.value })} data-testid="edit-emergency-name" />
        </Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Parentesco">
            <CatalogSel value={form.emergency_contact_relationship} options={C("relationships")} onChange={(v) => patch({ emergency_contact_relationship: v })} testid="edit-relationship" />
          </Fld>
          <Fld label="Teléfono">
            <Input value={form.emergency_contact_phone || ""} onChange={(e) => patch({ emergency_contact_phone: e.target.value })} data-testid="edit-emergency-phone" />
          </Fld>
        </div>
      </>
    );
  }

  if (section === "contract") {
    return (
      <>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Fecha de ingreso">
            <DatePickerEs value={form.hire_date || ""} onChange={(iso) => patch({ hire_date: iso })} allowClear={false} testId="edit-hire-date" />
          </Fld>
          <Fld label="Empresa">
            <Select value={form.company_id || "none"} onValueChange={(v) => patch({ company_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="edit-company"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin empresa</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}{c.is_primary ? " ★" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Lugar de trabajo / Sucursal">
            <Select value={form.primary_branch_id || ""} onValueChange={(v) => patch({ primary_branch_id: v })}>
              <SelectTrigger data-testid="edit-branch"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Departamento">
            <Select value={form.department_id || "none"} onValueChange={(v) => patch({ department_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="edit-department"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin departamento</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Ocupación">
            <Select value={form.occupation_id || "none"} onValueChange={(v) => patch({ occupation_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="edit-occupation"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin ocupación</SelectItem>
                {occupations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.acronym ? ` (${o.acronym})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Tipo de contrato">
            <CatalogSel value={form.contract_type} options={C("contract-types")} onChange={(v) => patch({ contract_type: v })} testid="edit-contract-type" />
          </Fld>
          <Fld label="Estado">
            <Select value={form.status || "active"} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="inactive">Inactivo</SelectItem>
                <SelectItem value="suspended">Suspendido</SelectItem>
                <SelectItem value="terminated">Terminado</SelectItem>
              </SelectContent>
            </Select>
          </Fld>
        </div>
        {form.contract_type && form.contract_type !== "CTI" && (
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Motivo / obra del plazo">
              <Input value={form.contract_reason || ""} onChange={(e) => patch({ contract_reason: e.target.value })} placeholder="Ej: Sustituir vacaciones" data-testid="edit-contract-reason" />
            </Fld>
            <Fld label="Fecha de finalización">
              <DatePickerEs value={form.contract_end_date || ""} onChange={(iso) => patch({ contract_end_date: iso })} testId="edit-contract-end-date" />
            </Fld>
          </div>
        )}
      </>
    );
  }

  if (section === "conditions") {
    const sg = form.social_guarantees || { ccss: false, ins: false };
    const setSG = (p) => patch({ social_guarantees: { ...sg, ...p } });
    return (
      <>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Jornada laboral">
            <CatalogSel value={form.journey_type} options={C("journey-types")} onChange={(v) => patch({ journey_type: v })} testid="edit-journey-type" />
          </Fld>
          <Fld label="Manera de jornada">
            <CatalogSel value={form.journey_mode} options={C("journey-modes")} onChange={(v) => patch({ journey_mode: v })} testid="edit-journey-mode" />
          </Fld>
        </div>
        <Fld label="Garantías sociales">
          <div className="flex gap-5 mt-1">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-800">
              <input type="checkbox" checked={!!sg.ccss} onChange={(e) => setSG({ ccss: e.target.checked })} data-testid="edit-ccss" /> CCSS
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-800">
              <input type="checkbox" checked={!!sg.ins} onChange={(e) => setSG({ ins: e.target.checked })} data-testid="edit-ins" /> INS
            </label>
          </div>
        </Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Horario de trabajo">
            <CatalogSel value={form.work_schedule_type} options={C("work-schedule-types")} onChange={(v) => patch({ work_schedule_type: v })} testid="edit-schedule-type" />
          </Fld>
          <Fld label="Jornada configurada">
            <Select value={form.schedule_id || "none"} onValueChange={(v) => patch({ schedule_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="edit-schedule"><SelectValue placeholder="Ninguna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ninguna</SelectItem>
                {schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}-{s.end_time})</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
        </div>
        <WorkScheduleEditor
          workDays={form.work_days || []}
          daySchedules={form.day_schedules || {}}
          weekDays={C("week-days")}
          journeyType={form.journey_type}
          accepted={form.schedule_accepted}
          onAcceptedChange={(v) => patch({ schedule_accepted: v })}
          onChange={(v) => patch(v)}
        />
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 mb-2 block">
            Tiempos de descanso (minutos)
          </Label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "breakfast", label: "Desayuno" },
              { key: "lunch", label: "Almuerzo" },
              { key: "refrigerio", label: "Refrigerio" },
            ].map((b) => {
              const bs = form.break_schedule || {};
              const cur = (bs[b.key] && bs[b.key].minutes) ?? 0;
              return (
                <div key={b.key}>
                  <p className="text-[11px] text-zinc-600 mb-1">{b.label}</p>
                  <Input
                    type="number"
                    min="0"
                    step="5"
                    value={cur}
                    onChange={(e) =>
                      patch({
                        break_schedule: {
                          ...(form.break_schedule || {}),
                          [b.key]: { minutes: Math.max(0, Number(e.target.value) || 0) },
                        },
                      })
                    }
                    className="h-8 text-xs"
                    data-testid={`edit-break-${b.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  if (section === "payment") {
    const dedList = form.authorized_deductions || [];
    const otherList = form.other_deductions || [];
    const toggleDed = (val) => {
      const next = dedList.includes(val) ? dedList.filter((x) => x !== val) : [...dedList, val];
      patch({ authorized_deductions: next });
    };
    const addOther = () => {
      const v = (form._otherInput || "").trim();
      if (!v || otherList.some((x) => x.toLowerCase() === v.toLowerCase())) {
        patch({ _otherInput: "" });
        return;
      }
      patch({ other_deductions: [...otherList, v], _otherInput: "" });
    };
    return (
      <>
        <Fld label="Salario base (₡)">
          <Input type="number" value={form.base_salary || 0} onChange={(e) => patch({ base_salary: e.target.value })} data-testid="edit-base-salary" />
        </Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Fijación salarial">
            <div
              className="h-9 px-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-700"
              data-testid="edit-fixation-display"
            >
              {(() => {
                const occ = occupations.find((o) => o.id === form.occupation_id);
                const st = occ?.salary_type || form.salary_fixation || "monthly";
                return st === "daily" ? "Diaria" : "Mensual";
              })()}
              <span className="ml-auto text-[10px] text-zinc-400">según ocupación</span>
            </div>
          </Fld>
          <Fld label="Forma de pago">
            <CatalogSel value={form.payment_method} options={C("payment-methods")} onChange={(v) => patch({ payment_method: v })} testid="edit-payment-method" />
          </Fld>
        </div>
        <Fld label="Periodicidad">
          <CatalogSel value={form.payment_periodicity} options={C("payment-periodicities")} onChange={(v) => patch({ payment_periodicity: v })} testid="edit-periodicity" />
        </Fld>
        <Fld label="Cuenta bancaria / IBAN">
          <Input value={form.bank_account || ""} onChange={(e) => patch({ bank_account: e.target.value })} data-testid="edit-bank" />
        </Fld>
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 mb-2 block">
            Deducciones autorizadas
          </Label>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { value: "VALES", label: "Vales / Anticipos" },
              { value: "GARANTIAS_SOCIALES", label: "Garantías sociales (CCSS, INS)" },
              { value: "SUSPENCIONES", label: "Suspensiones disciplinarias" },
              { value: "OTRO", label: "Otras deducciones autorizadas" },
            ].map((d) => (
              <label key={d.value} className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer" data-testid={`edit-deduction-${d.value}`}>
                <input type="checkbox" checked={dedList.includes(d.value)} onChange={() => toggleDed(d.value)} />
                {d.label}
              </label>
            ))}
          </div>
          {dedList.includes("OTRO") && (
            <div className="mt-3 pt-3 border-t border-zinc-200" data-testid="edit-other-deductions">
              <Label className="text-[11px] font-medium text-zinc-700 mb-1.5 block">Detalle de otras deducciones</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={form._otherInput || ""}
                  onChange={(e) => patch({ _otherInput: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOther(); } }}
                  placeholder="Ej: Préstamo cooperativa"
                  className="h-8 text-xs"
                  data-testid="edit-other-deduction-input"
                />
                <Button type="button" size="sm" variant="outline" onClick={addOther} className="h-8 px-3 text-xs shrink-0" data-testid="edit-other-deduction-add">
                  Agregar
                </Button>
              </div>
              {otherList.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {otherList.map((d, i) => (
                    <span key={`${d}-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-zinc-300 pl-2.5 pr-1 py-0.5 text-xs text-zinc-700" data-testid={`edit-other-deduction-chip-${i}`}>
                      {d}
                      <button type="button" onClick={() => patch({ other_deductions: otherList.filter((_, j) => j !== i) })} className="rounded-full hover:bg-zinc-100 w-4 h-4 flex items-center justify-center text-zinc-400 hover:text-zinc-700" aria-label={`Quitar ${d}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// Employee Documents Panel (Documentos tab)
// ─────────────────────────────────────────────────────────────────────────
function DocExpiryBadge({ expiresAt }) {
  if (!expiresAt) return null;
  // Calculate days remaining (UTC-safe enough for daily granularity)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(`${expiresAt}T00:00:00`);
  const daysLeft = Math.round((exp - today) / (1000 * 60 * 60 * 24));
  let tone;
  let label;
  if (daysLeft < 0) {
    tone = "bg-red-100 text-red-700 border-red-200";
    label = `Vencido hace ${Math.abs(daysLeft)}d`;
  } else if (daysLeft <= 7) {
    tone = "bg-red-50 text-red-700 border-red-200";
    label = `Vence en ${daysLeft}d`;
  } else if (daysLeft <= 30) {
    tone = "bg-amber-50 text-amber-700 border-amber-200";
    label = `Vence en ${daysLeft}d`;
  } else {
    tone = "bg-zinc-50 text-zinc-600 border-zinc-200";
    label = `Vence ${expiresAt}`;
  }
  return (
    <span
      className={`inline-block mt-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${tone}`}
      data-testid="document-expiry-badge"
    >
      {label}
    </span>
  );
}


function EmployeeDocumentsPanel({ employeeId, documentTypes }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docType, setDocType] = useState("id_doc");
  const [docFile, setDocFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [types, setTypes] = useState(documentTypes || []);
  const [addingType, setAddingType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [savingType, setSavingType] = useState(false);
  const [preview, setPreview] = useState(null); // {url, name, isImage}

  useEffect(() => { setTypes(documentTypes || []); }, [documentTypes]);

  const fileUrlFor = (p) => {
    const BASE = process.env.REACT_APP_BACKEND_URL;
    return `${BASE}/api/files/object/${p}`;
  };

  const loadDocs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/documents`);
      setDocs(data);
    } catch {
      toast.error("Error al cargar documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (employeeId) loadDocs(); /* eslint-disable-next-line */ }, [employeeId]);

  const typeLabel = (code) => types.find((t) => t.code === code)?.label || code;

  const handleAddType = async () => {
    const label = newTypeLabel.trim();
    if (!label) { toast.error("Escriba el nombre del tipo"); return; }
    setSavingType(true);
    try {
      const { data } = await api.post(`/catalogs/document-types`, { label });
      setTypes((prev) => (prev.some((t) => t.code === data.code) ? prev : [...prev, data]));
      setDocType(data.code);
      setNewTypeLabel("");
      setAddingType(false);
      toast.success("Tipo de documento agregado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar el tipo");
    } finally {
      setSavingType(false);
    }
  };

  const handleUpload = async () => {
    if (!docFile) { toast.error("Seleccione un archivo"); return; }
    const fd = new FormData();
    fd.append("file", docFile);
    fd.append("document_type", docType);
    fd.append("notes", notes);
    if (expiresAt) fd.append("expires_at", expiresAt);
    setUploading(true);
    try {
      await api.post(`/employees/${employeeId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento subido");
      setUploadOpen(false);
      setDocFile(null);
      setNotes("");
      setExpiresAt("");
      setDocType("id_doc");
      loadDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/employees/${employeeId}/documents/${docId}`);
      toast.success("Documento eliminado");
      loadDocs();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const sizeKB = (n) => n ? `${(n / 1024).toFixed(0)} KB` : "—";

  return (
    <div data-testid="employee-documents-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-950">Documentos del expediente</h2>
          <p className="text-xs text-zinc-500">
            Contrato, cédula, INS, CCSS, delincuencia, CV, cuenta IBAN y otros.
          </p>
        </div>
        <Button
          onClick={() => setUploadOpen(true)}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="upload-document-button"
        >
          <Upload className="w-4 h-4 mr-2" /> Subir documento
        </Button>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          {loading ? (
            <p className="text-sm text-zinc-400 text-center py-10">Cargando…</p>
          ) : docs.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Este trabajador aún no tiene documentos cargados</p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {docs.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50/60 transition-colors"
                  data-testid={`document-row-${d.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                    <FileIcon className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {typeLabel(d.document_type)}
                    </p>
                    <p className="text-xs text-zinc-500 truncate">
                      {d.original_filename} · {sizeKB(d.size)} · {formatDate(d.created_at)}
                    </p>
                    {d.expires_at && <DocExpiryBadge expiresAt={d.expires_at} />}
                    {d.notes && <p className="text-xs text-zinc-400 italic mt-0.5 truncate">{d.notes}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreview({
                      url: fileUrlFor(d.storage_path),
                      name: typeLabel(d.document_type),
                      isImage: (d.content_type || "").startsWith("image/"),
                    })}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-zinc-100 text-zinc-500"
                    title="Ver sin descargar"
                    data-testid={`preview-document-${d.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <a
                    href={fileUrlFor(d.storage_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={d.original_filename || true}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-zinc-100 text-zinc-500"
                    title="Descargar"
                    data-testid={`view-document-${d.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDelete(d.id)}
                    data-testid={`delete-document-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <Upload className="w-5 h-5" /> Subir documento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <Fld label="Tipo de documento">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!addingType ? (
                <button
                  type="button"
                  onClick={() => setAddingType(true)}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
                  data-testid="add-document-type-toggle"
                >
                  <Plus className="w-3.5 h-3.5" /> ¿No está en la lista? Agregar tipo
                </button>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={newTypeLabel}
                    onChange={(e) => setNewTypeLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddType(); } }}
                    placeholder="Ej: Constancia de estudios"
                    autoFocus
                    data-testid="new-document-type-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddType}
                    disabled={savingType || !newTypeLabel.trim()}
                    className="bg-zinc-900 text-white hover:bg-zinc-800 shrink-0"
                    data-testid="new-document-type-save"
                  >
                    {savingType ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agregar"}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 shrink-0"
                    onClick={() => { setAddingType(false); setNewTypeLabel(""); }}
                    data-testid="new-document-type-cancel"
                  >
                    <X className="w-4 h-4 text-zinc-400" />
                  </Button>
                </div>
              )}
            </Fld>
            <Fld label="Archivo (PDF, imagen, docx — máx. 10 MB)">
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.gif,.txt,.csv,image/*,application/pdf"
                onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                data-testid="document-file-input"
              />
            </Fld>
            <Fld label="Notas (opcional)">
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej: Vencimiento 2027, revisado por…"
                data-testid="document-notes-input"
              />
            </Fld>
            <Fld label="Fecha de vencimiento (opcional · útil para licencias, INS, contratos)">
              <DatePickerEs
                value={expiresAt}
                onChange={setExpiresAt}
                placeholder="Sin fecha de vencimiento"
                testId="document-expires-input"
              />
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} data-testid="document-cancel">
              Cancelar
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!docFile || uploading}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="document-submit"
            >
              {uploading ? "Subiendo…" : "Subir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl w-[92vw] h-[88vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-3 border-b border-zinc-100">
            <DialogTitle className="text-zinc-950 flex items-center gap-2 text-base">
              <Eye className="w-4 h-4" /> {preview?.name || "Vista previa"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-zinc-50 overflow-auto flex items-center justify-center">
            {preview && (preview.isImage ? (
              <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" data-testid="preview-image" />
            ) : (
              <object data={preview.url} type="application/pdf" className="w-full h-full" data-testid="preview-frame">
                <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-6">
                  <FileText className="w-10 h-10 text-zinc-300" />
                  <p className="text-sm text-zinc-500">No se pudo mostrar la vista previa aquí.</p>
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-zinc-900 underline"
                  >
                    Abrir en una pestaña nueva
                  </a>
                </div>
              </object>
            ))}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-zinc-100 sm:justify-between gap-2">
            {preview && (
              <a
                href={preview.url}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
                data-testid="preview-download"
              >
                <Download className="w-4 h-4" /> Descargar
              </a>
            )}
            <Button
              variant="outline"
              onClick={() => setPreview(null)}
              data-testid="preview-close"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
